import { Boxes, Check, SearchX } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/intl';
import { cn } from '@/lib/utils';
import { problemMessage } from '../api/problem';
import { StateMessage } from '../ui/StateMessage';
import { moduleIcon, useModuleText } from './module-text';
import type { useModuleActs } from './use-module-acts';
import type { ModuleHistoryEntry, ModuleState, useModuleHistory, useModules } from './use-modules';

export const MODULE_TONE: Record<ModuleState['state'], string> = {
  here: 'success',
  from_tenant: 'info',
  not_active: '',
};

export function ModuleItem({
  item,
  cardHref,
  acts,
}: {
  readonly item: ModuleState;
  readonly cardHref: string;
  readonly acts: ReturnType<typeof useModuleActs>;
}) {
  const { t } = useTranslation('administration');
  const { name, description } = useModuleText();
  const Icon = moduleIcon(item.module.code);
  const selected = useMatch(cardHref.split('?')[0] ?? cardHref) !== null;
  const pending = acts.isPending(item);
  const desc = description(item.module);

  return (
    <li className={cn('module-card', selected && 'module-card-selected')} data-state={item.state}>
      <div className="module-card-main">
        <span aria-hidden="true" className="module-card-icon">
          <Icon className="size-5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2.5">
            <Link
              to={cardHref}
              className="module-card-name"
              aria-current={selected ? 'page' : undefined}
            >
              {name(item.module)}
            </Link>
            <span className={cn('status-badge', MODULE_TONE[item.state])}>
              {t(`moduleSection.state.${item.state}`)}
            </span>
          </span>
          {desc !== null ? <span className="module-card-desc">{desc}</span> : null}
          <span className="module-card-line">
            <ModuleLine item={item} />
          </span>
        </span>
      </div>
      {item.can.activate || item.can.deactivate ? (
        <span className="module-card-act">
          {item.can.activate ? (
            <Button disabled={pending} onClick={() => void acts.activate(item)}>
              <Check />
              {t('moduleSection.activate')}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="text-destructive"
              disabled={pending}
              onClick={() => void acts.deactivate(item)}
            >
              {t('moduleSection.deactivate')}
            </Button>
          )}
        </span>
      ) : null}
    </li>
  );
}

/** The state's one line: since when and by whom, or where it is changed. */
export function ModuleLine({ item }: { readonly item: ModuleState }) {
  const { t } = useTranslation('administration');
  if (item.state === 'not_active') {
    return <>{t('moduleSection.line.notActive')}</>;
  }
  if (item.state === 'from_tenant') {
    return <>{t('moduleSection.line.fromTenant')}</>;
  }
  const date = item.since !== null ? formatDate(item.since) : null;
  if (date === null) {
    return <>{t('moduleSection.line.hereUnknown')}</>;
  }
  return (
    <>
      {item.by !== null
        ? t('moduleSection.line.hereBy', { by: item.by, date })
        : t('moduleSection.line.here', { date })}
    </>
  );
}

function HistoryBody({
  history,
  hereId,
}: {
  readonly history: ReturnType<typeof useModuleHistory>;
  readonly hereId: string;
}) {
  const { t } = useTranslation('administration');
  const { state, more } = history;
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
        {[0, 1, 2].map((n) => (
          <Skeleton key={n} className="h-10 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.items.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {state.status === 'error'
          ? t('moduleSection.history.error')
          : t('moduleSection.history.empty')}
      </p>
    );
  }
  return (
    <>
      <HistoryList entries={state.items} hereId={hereId} />
      {state.status === 'ready' && state.hasMore ? (
        <Button variant="outline" size="sm" onClick={more} disabled={state.loadingMore}>
          {state.loadingMore ? t('common.loading') : t('common.more')}
        </Button>
      ) : null}
    </>
  );
}

export function HistoryAside({
  history,
  hereId,
}: {
  readonly history: ReturnType<typeof useModuleHistory>;
  readonly hereId: string;
}) {
  const { t } = useTranslation('administration');
  const titleId = useId();
  return (
    <aside aria-labelledby={titleId} className="module-history">
      <h2 id={titleId} className="module-history-title">
        {t('moduleSection.history.title')}
      </h2>
      <HistoryBody history={history} hereId={hereId} />
    </aside>
  );
}

export function HistoryList({
  entries,
  hereId,
}: {
  readonly entries: readonly ModuleHistoryEntry[];
  /** The place shown: its own acts read «qui», the tenant's name the tenant. */
  readonly hereId: string;
}) {
  const { t } = useTranslation('administration');
  const { name } = useModuleText();
  return (
    <ol className="module-history-list">
      {entries.map((entry) => (
        <li key={entry.id}>
          <span className="module-history-date">{formatDate(entry.at)}</span>
          <span className="module-history-what">
            {t(
              entry.place.id?.toLowerCase() === hereId.toLowerCase()
                ? 'moduleSection.history.act'
                : 'moduleSection.history.actTenant',
              {
                module: name(entry.module),
                act: t(`moduleSection.history.${entry.act}`),
                place: entry.place.name,
              },
            )}
          </span>
          <span className="module-history-who">
            {entry.by ?? t('moduleSection.history.byUnknown')}
          </span>
        </li>
      ))}
    </ol>
  );
}

type ModulesState = ReturnType<typeof useModules>['state'];

/** The module cards, or the loading, error, needs-an-organization or no-match state. */
export function ModuleListPane({
  state,
  shown,
  reload,
  placeName,
  onClear,
  acts,
  cardHrefFor,
}: {
  readonly state: ModulesState;
  readonly shown: readonly ModuleState[];
  readonly reload: () => void;
  readonly placeName: string;
  readonly onClear: () => void;
  readonly acts: ReturnType<typeof useModuleActs>;
  readonly cardHrefFor: (item: ModuleState) => string;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
        {[0, 1, 2].map((n) => (
          <Skeleton key={n} className="h-24 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Boxes}
        title={t('moduleSection.loadError')}
        text={problemMessage(t, state.code, state.httpStatus)}
        action={
          <Button variant="outline" onClick={reload}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  if (state.needsOrganization) {
    return (
      <StateMessage
        icon={Boxes}
        title={t('moduleSection.needsOrganization.title')}
        text={t('moduleSection.needsOrganization.text', { place: placeName })}
      />
    );
  }
  if (shown.length === 0) {
    return (
      <StateMessage
        icon={SearchX}
        title={t('moduleSection.noResults')}
        action={
          <Button variant="outline" onClick={onClear}>
            {t('invitations.noResults.clear')}
          </Button>
        }
      />
    );
  }
  return (
    <ul className="module-list" aria-label={t('moduleSection.listLabel', { place: placeName })}>
      {shown.map((item) => (
        <ModuleItem key={item.module.id} item={item} cardHref={cardHrefFor(item)} acts={acts} />
      ))}
    </ul>
  );
}
